import React, { useEffect, useState } from "react";
import { collection, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";

export default function NotificationsPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "users"));
        const usersWithToken = snap.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((u) => u.expoPushToken);
        setUsers(usersWithToken);
      } catch (e) {
        alert("Erreur lors de la récupération des utilisateurs: " + e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, []);

  const sendNotification = async () => {
    if (!title.trim() || !body.trim()) {
      alert("Le titre et le message sont obligatoires");
      return;
    }
    if (selectedUsers.length === 0) {
      alert("Veuillez sélectionner au moins un destinataire");
      return;
    }
    setSending(true);

    try {
      const messages = selectedUsers.map((user) => ({
        to: user.expoPushToken,
        sound: "default",
        title,
        body,
        data: { extra: "notif" },
      }));

      // Envoi batch via le proxy PHP
      const apiUrl = window.location.hostname === "localhost"
        ? "http://localhost/site%20zzigs/API/v1/send_notification.php"
        : "https://zzigs.com/api/v1/send_notification.php";

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(messages),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Erreur serveur");
      }

      // Compter les succes/echecs depuis la reponse Expo
      let successCount = 0;
      let failCount = 0;
      if (result.data) {
        result.data.forEach((r) => {
          if (r.status === "ok") successCount++;
          else failCount++;
        });
      } else {
        successCount = selectedUsers.length;
      }

      // Sauvegarder dans Firestore
      for (const user of selectedUsers) {
        await addDoc(collection(db, "notifications"), {
          title,
          text: body,
          userId: user.id,
          isNew: true,
          type: "admin_notification",
          createdAt: serverTimestamp(),
        });
      }

      const successDiv = document.createElement("div");
      successDiv.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
      successDiv.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Notification envoyée à ${successCount} utilisateur${
        successCount > 1 ? "s" : ""
      }${failCount > 0 ? ` (${failCount} échec${failCount > 1 ? "s" : ""})` : ""} !
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 3000);

      setTitle("");
      setBody("");
      setSelectedUsers([]);
    } catch (e) {
      alert("Erreur lors de l'envoi : " + e.message);
    }

    setSending(false);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers([...users]);
    }
  };

  const toggleUser = (user) => {
    const isSelected = selectedUsers.some((u) => u.id === user.id);
    if (isSelected) {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const UserSelectionModal = () => {
    const [searchTerm, setSearchTerm] = useState("");

    if (!showModal) return null;

    const filteredUsers = users.filter((user) => {
      const searchLower = searchTerm.toLowerCase();
      const fullName = `${user.firstname || ""} ${user.lastname || ""}`.toLowerCase();
      const email = (user.email || "").toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">
                Sélectionner les destinataires
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Fermer
              </button>
            </div>
            <input
              type="text"
              placeholder="Rechercher par nom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
              autoFocus
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {selectedUsers.length}/{users.length} sélectionné{selectedUsers.length > 1 ? "s" : ""}
              </span>
              <button
                onClick={toggleSelectAll}
                className="text-xs font-medium text-[#801111] hover:text-[#5a0c0c]"
              >
                {selectedUsers.length === users.length ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {filteredUsers.length === 0 ? (
              <p className="text-center py-8 text-sm text-gray-500">Aucun utilisateur trouvé</p>
            ) : (
              filteredUsers.map((user) => {
                const isSelected = selectedUsers.some((u) => u.id === user.id);
                return (
                  <div
                    key={user.id}
                    onClick={() => toggleUser(user)}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-red-50 border border-[#801111]"
                        : "hover:bg-gray-50 border border-transparent"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? "bg-[#801111] border-[#801111]"
                          : "border-gray-300"
                      }`}
                    >
                      {isSelected && (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-white">
                          <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    {user.profilPhotoUrl ? (
                      <img src={user.profilPhotoUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 text-[10px] font-medium">
                        {user.firstname?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">
                        {user.firstname && user.lastname
                          ? `${user.firstname} ${user.lastname}`
                          : "Nom non renseigné"}
                      </p>
                      <p className="text-[10px] text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="p-3 border-t border-gray-200 flex justify-end gap-2">
            <button
              onClick={() => setShowModal(false)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 font-medium hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-1.5 bg-[#801111] text-white rounded-md text-xs font-medium hover:bg-[#5a0c0c]"
            >
              Confirmer ({selectedUsers.length})
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          <UserSelectionModal />

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Avec notifs</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{users.length}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Destinataires</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{selectedUsers.length}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Type</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">
                {selectedUsers.length === users.length && selectedUsers.length > 0
                  ? "Broadcast"
                  : selectedUsers.length > 1
                  ? "Multiple"
                  : selectedUsers.length === 1
                  ? "Individuel"
                  : "-"}
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-3 py-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-900">Composer la notification</span>
            </div>
            <div className="p-3 space-y-3">
              {/* Recipient */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Destinataires</label>
                <button
                  onClick={() => setShowModal(true)}
                  disabled={loading}
                  className="w-full flex items-center justify-between p-2 border border-gray-300 rounded-md hover:border-gray-400 transition-colors text-left"
                >
                  <span className="text-xs text-gray-600">
                    {selectedUsers.length === 0
                      ? "Sélectionner les destinataires"
                      : `${selectedUsers.length} utilisateur${selectedUsers.length > 1 ? "s" : ""} sélectionné${selectedUsers.length > 1 ? "s" : ""}`}
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                  </svg>
                </button>

                {selectedUsers.length > 0 && selectedUsers.length <= 5 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {selectedUsers.map((user) => (
                      <span
                        key={user.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px]"
                      >
                        {user.firstname && user.lastname
                          ? `${user.firstname} ${user.lastname}`
                          : user.email}
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleUser(user); }}
                          className="hover:text-red-600"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Titre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={sending || loading}
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:opacity-50"
                  placeholder="Ex: Nouvelle fonctionnalité disponible !"
                  maxLength={65}
                />
                <p className="text-[10px] text-gray-400 mt-0.5">{title.length}/65</p>
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={4}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  disabled={sending || loading}
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:opacity-50 resize-none"
                  placeholder="Ex: Découvrez notre nouvelle fonctionnalité..."
                  maxLength={200}
                />
                <p className="text-[10px] text-gray-400 mt-0.5">{body.length}/200</p>
              </div>

              {/* Preview */}
              {(title || body) && (
                <div className="p-3 bg-gray-50 rounded-md border border-dashed border-gray-300">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">Aperçu</p>
                  <div className="bg-white rounded-md shadow p-3">
                    <p className="text-xs font-semibold text-gray-900">{title || "Titre"}</p>
                    <p className="text-[11px] text-gray-600 mt-0.5">{body || "Message..."}</p>
                    <p className="text-[10px] text-gray-400 mt-1">À l'instant</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
                <button
                  onClick={() => { setTitle(""); setBody(""); setSelectedUsers([]); }}
                  disabled={sending || loading}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  Réinitialiser
                </button>
                <button
                  onClick={sendNotification}
                  disabled={sending || loading || !title.trim() || !body.trim() || selectedUsers.length === 0}
                  className="px-4 py-1.5 bg-[#801111] text-white rounded-md text-xs font-medium hover:bg-[#5a0c0c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? "Envoi en cours..." : "Envoyer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}
